import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RcsActiveAgent, RcsAgent, RcsAgentDraft } from 'src/app/models/Rcs';
import { environment } from 'src/environments/environment';

/** Arquivos da ficha: logo e banner novos, ou o id do agente de onde copiar os dois. */
export interface RcsAgentAssets {
  logo?: File | null;
  banner?: File | null;
  copyAssetsFrom?: string | null;
}

/**
 * Agentes RCS do cliente (`/api/rcs/agents`).
 *
 * Não existe editar: a ficha travada depois de enviada é regra do RCS, e a
 * API responde 405 no PUT/PATCH. Mudar marca é cadastrar agente novo.
 */
@Injectable({ providedIn: 'root' })
export class RcsAgentService {

  private readonly baseUrl = `${environment.apiUrl}/rcs/agents`;

  constructor(private http: HttpClient) {}

  list(): Observable<RcsAgent[]> {
    return this.http.get<RcsAgent[]>(this.baseUrl);
  }

  /** Só os ativos: são os únicos que o envio e a automação deixam escolher. */
  listActive(): Observable<RcsActiveAgent[]> {
    return this.http.get<RcsActiveAgent[]>(`${this.baseUrl}/active`);
  }

  find(id: string): Observable<RcsAgent> {
    return this.http.get<RcsAgent>(`${this.baseUrl}/${id}`);
  }

  /** Multipart: os arquivos não cabem em JSON. */
  create(draft: RcsAgentDraft, assets: RcsAgentAssets): Observable<RcsAgent> {
    const body = new FormData();

    Object.entries(draft).forEach(([key, value]) => body.append(key, value == null ? '' : String(value)));

    if (assets.logo) {
      body.append('logo', assets.logo);
    }

    if (assets.banner) {
      body.append('banner', assets.banner);
    }

    if (assets.copyAssetsFrom) {
      body.append('copy_assets_from', assets.copyAssetsFrom);
    }

    return this.http.post<RcsAgent>(this.baseUrl, body);
  }

  // --- Acompanhamento (17tm466frtx) ---------------------------------------------

  /**
   * "Já aprovei": o cliente confirma que clicou em "Aprovar" no e-mail da ABR
   * Telecom. Sincroniza com a Pingoo na hora e devolve o agente já atualizado.
   * 422 se o agente não estiver em `brand_verification` (ex.: outra aba já confirmou).
   */
  ownerConfirmed(id: string): Observable<RcsAgent> {
    return this.http.post<RcsAgent>(`${this.baseUrl}/${id}/owner-confirmed`, {});
  }

  /**
   * "Reenviar e-mail": pede para a ABR Telecom mandar de novo o e-mail de
   * verificação. A API não reenvia de fato (ela não tem esse endpoint); o pedido só
   * fica registrado para o time olhar. 422 fora de `brand_verification`, 429 se já
   * pediu há menos de 24 h (mensagem amigável do backend).
   */
  resendVerification(id: string): Observable<RcsAgent> {
    return this.http.post<RcsAgent>(`${this.baseUrl}/${id}/resend-verification`, {});
  }

  /** "Encaminhar passo a passo": manda por e-mail, para o usuário ou para o e-mail informado. */
  sendInstructions(id: string, email?: string | null): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.baseUrl}/${id}/send-instructions`, email ? { email } : {});
  }
}
